// ===========================================
// Product Hooks
// ===========================================
// React Query hooks for product catalog (Phase 2 - Product Sales)
//
// Products can be sold individually or added to family invoices.
// All product management operations are admin-only.
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProducts,
  getProduct,
  getLowStockProducts,
  getProductsByCategory,
  createProduct,
  updateProduct,
  deleteProduct,
  adjustInventory,
  checkAvailability,
  uploadProductImage,
  deleteProductImage,
  previewAddProductToInvoices,
  addProductToInvoices,
  getProductCategories,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
  Product,
  CreateProductInput,
  UpdateProductInput,
  ProductFilters,
  InventoryAdjustmentInput,
  AddProductToInvoicesInput,
  ProductCategoryItem,
  CreateProductCategoryData,
  UpdateProductCategoryData,
} from '../api/product.api';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const productKeys = {
  all: ['products'] as const,
  // Product lists
  list: (filters?: ProductFilters) => [...productKeys.all, 'list', filters] as const,
  lowStock: () => [...productKeys.all, 'low-stock'] as const,
  byCategory: () => [...productKeys.all, 'by-category'] as const,
  // Single product
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
  // Availability check
  availability: (id: string, quantity: number) =>
    [...productKeys.all, 'availability', id, quantity] as const,
  // Categories
  categories: () => [...productKeys.all, 'categories'] as const,
};

// ===========================================
// LIST HOOKS
// ===========================================

/**
 * Get all products with optional filters
 * @param filters - Optional filters (category, isActive, search, trackInventory)
 */
export function useProducts(filters?: ProductFilters) {
  return useQuery({
    queryKey: productKeys.list(filters),
    queryFn: () => getProducts(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Get products with low stock alerts
 */
export function useLowStockProducts() {
  return useQuery({
    queryKey: productKeys.lowStock(),
    queryFn: getLowStockProducts,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Get products grouped by category
 */
export function useProductsByCategory() {
  return useQuery({
    queryKey: productKeys.byCategory(),
    queryFn: getProductsByCategory,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// ===========================================
// SINGLE PRODUCT HOOKS
// ===========================================

/**
 * Get a single product by ID
 * @param id - Product ID
 * @param options - Query options
 */
export function useProduct(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => getProduct(id),
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled !== false && !!id,
  });
}

// ===========================================
// MUTATION HOOKS
// ===========================================

/**
 * Create a new product
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductInput) => createProduct(data),
    onSuccess: () => {
      // Invalidate all product lists to refresh data
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Update a product
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductInput }) => updateProduct(id, data),
    onSuccess: (updatedProduct, variables) => {
      // Update the specific product in cache
      queryClient.setQueryData(productKeys.detail(variables.id), updatedProduct);
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Delete a product
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: productKeys.detail(deletedId) });
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Adjust product inventory
 */
export function useAdjustInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InventoryAdjustmentInput }) =>
      adjustInventory(id, data),
    onSuccess: (updatedProduct, variables) => {
      // Update the specific product in cache
      queryClient.setQueryData(productKeys.detail(variables.id), updatedProduct);
      // Invalidate lists to refresh (especially low-stock)
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Check product availability for a given quantity
 */
export function useCheckAvailability(id: string, quantity: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: productKeys.availability(id, quantity),
    queryFn: () => checkAvailability(id, quantity),
    staleTime: 10 * 1000, // 10 seconds
    enabled: options?.enabled !== false && !!id && quantity > 0,
  });
}

// ===========================================
// IMAGE MUTATION HOOKS
// ===========================================

/**
 * Upload image for a product
 */
export function useUploadProductImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, file }: { productId: string; file: File }) =>
      uploadProductImage(productId, file),
    onSuccess: (_, variables) => {
      // Invalidate the specific product to refresh its image
      queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.productId) });
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Delete image from a product
 */
export function useDeleteProductImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => deleteProductImage(productId),
    onSuccess: (_, productId) => {
      // Invalidate the specific product to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

// ===========================================
// ADD TO INVOICE HOOKS
// ===========================================

/**
 * Preview adding a product to invoices
 */
export function usePreviewAddProductToInvoices() {
  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: AddProductToInvoicesInput }) =>
      previewAddProductToInvoices(productId, data),
  });
}

/**
 * Execute adding a product to invoices
 */
export function useAddProductToInvoices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: AddProductToInvoicesInput }) =>
      addProductToInvoices(productId, data),
    onSuccess: () => {
      // Invalidate products (inventory may have changed)
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Invalidate invoices (new invoices may have been created)
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

// ===========================================
// COMBINED HOOKS
// ===========================================

/**
 * Combined hook for product management in ProductsTab
 * Provides all data and actions needed for the products page
 */
export function useProductManagement(filters?: ProductFilters) {
  const productsQuery = useProducts(filters);
  const lowStockQuery = useLowStockProducts();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const adjustInventoryMutation = useAdjustInventory();
  const uploadImageMutation = useUploadProductImage();
  const deleteImageMutation = useDeleteProductImage();

  return {
    // Data
    products: productsQuery.data ?? [],
    lowStockProducts: lowStockQuery.data ?? [],
    lowStockCount: lowStockQuery.data?.length ?? 0,
    // Loading states
    isLoading: productsQuery.isLoading,
    isLoadingLowStock: lowStockQuery.isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isAdjustingInventory: adjustInventoryMutation.isPending,
    isUploadingImage: uploadImageMutation.isPending,
    isDeletingImage: deleteImageMutation.isPending,
    // Error states
    error: productsQuery.error,
    lowStockError: lowStockQuery.error,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    adjustInventoryError: adjustInventoryMutation.error,
    uploadImageError: uploadImageMutation.error,
    deleteImageError: deleteImageMutation.error,
    // Actions
    create: createMutation.mutateAsync,
    update: (id: string, data: UpdateProductInput) => updateMutation.mutateAsync({ id, data }),
    remove: deleteMutation.mutateAsync,
    adjustInventory: (id: string, data: InventoryAdjustmentInput) =>
      adjustInventoryMutation.mutateAsync({ id, data }),
    uploadImage: (productId: string, file: File) =>
      uploadImageMutation.mutateAsync({ productId, file }),
    deleteImage: deleteImageMutation.mutateAsync,
    // Refetch
    refetch: () => {
      productsQuery.refetch();
      lowStockQuery.refetch();
    },
  };
}

/**
 * Combined hook for product selection in invoice creation
 * Provides products grouped by category for easy selection
 */
export function useProductSelection() {
  const byCategoryQuery = useProductsByCategory();

  const getProductsForCategory = (categoryId: string): Product[] => {
    if (!byCategoryQuery.data) return [];
    return byCategoryQuery.data.find((g) => g.categoryId === categoryId)?.products ?? [];
  };

  const getAllActiveProducts = (): Product[] => {
    if (!byCategoryQuery.data) return [];
    return byCategoryQuery.data.flatMap((g) => g.products);
  };

  return {
    // Data
    productsByCategory: byCategoryQuery.data,
    // Helper functions
    getProductsForCategory,
    getAllActiveProducts,
    // Loading state
    isLoading: byCategoryQuery.isLoading,
    // Error state
    error: byCategoryQuery.error,
    // Refetch
    refetch: byCategoryQuery.refetch,
  };
}

// ===========================================
// PRODUCT CATEGORY HOOKS
// ===========================================

/**
 * `enabled`: the route needs operations_pricing:read (#272: a page asks only for what its user
 * may read). Default on, as every existing caller is a pricing page.
 */
export function useProductCategories(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: productKeys.categories(),
    queryFn: getProductCategories,
    staleTime: 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

export function useCreateProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProductCategoryData) => createProductCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.categories() });
    },
  });
}

export function useUpdateProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductCategoryData }) =>
      updateProductCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.categories() });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProductCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.categories() });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export type { ProductCategoryItem, CreateProductCategoryData, UpdateProductCategoryData };
